
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  startCardPoker,
  playCardPoker,
  getCardPokerHistory,
} from '../../../services/gameService';
import {
  Card,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Modal,
  InputGroup,
  FormControl,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaPlay,
  FaHistory,
  FaInfoCircle,
  FaRedo,
  FaCrown,
  FaCheck,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './CardPoker.css';

const CardPoker = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Estado principal
  const [betAmount, setBetAmount] = useState(1);
  const [playerHand, setPlayerHand] = useState([]);        // [{value,suit,card}]
  const [dealerUpcard, setDealerUpcard] = useState(null);  // {value,suit,card}
  const [dealerHand, setDealerHand] = useState([]);        // tras resolver
  const [playerResultName, setPlayerResultName] = useState('');
  const [dealerResultName, setDealerResultName] = useState('');
  const [result, setResult] = useState(null);              // 'win' | 'lose' | 'tie' | 'void'
  const [winnings, setWinnings] = useState(null);
  const [balance, setBalance] = useState(null);

  // Flags UI
  const [starting, setStarting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');
  const [showRules, setShowRules] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const inRound = useMemo(() => playerHand.length > 0 && !result, [playerHand, result]);
  const roundResolved = useMemo(() => !!result, [result]);

  const getCardImage = (code) => `${process.env.PUBLIC_URL}/images/games/cards/${code}.png`;

  const resetRound = () => {
    setPlayerHand([]);
    setDealerUpcard(null);
    setDealerHand([]);
    setPlayerResultName('');
    setDealerResultName('');
    setResult(null);
    setWinnings(null);
    setError('');
  };

  // ===== Fase 1: Start (cobra Ante, reparte 5/5, muestra upcard del crupier) =====
  const handleStart = async () => {
    setStarting(true);
    setError('');
    setResult(null);
    try {
      const data = await startCardPoker(Number(betAmount));
      // Esperado: { playerHand, dealerUpcard, newBalance }
      setPlayerHand(data.playerHand || []);
      setDealerUpcard(data.dealerUpcard || null);
      if (typeof data.newBalance === 'number') setBalance(data.newBalance);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        t('cardPoker.error_start', { defaultValue: 'Error starting Card Poker' });
      setError(msg);
    } finally {
      setStarting(false);
    }
  };

  // ===== Fase 2: Resolve (muestra showdown con dealerHand y resultado) =====
  const handleResolve = async () => {
    setResolving(true);
    setError('');
    try {
      // Opcional: pasamos betAmount; el servidor validará que coincida con la sesión
      const data = await playCardPoker(Number(betAmount));
      // Esperado:
      // { playerHand, dealerHand, playerResult, dealerResult, result, winnings, newBalance }
      if (Array.isArray(data.playerHand)) setPlayerHand(data.playerHand);
      if (Array.isArray(data.dealerHand)) setDealerHand(data.dealerHand);
      if (data.playerResult) setPlayerResultName(data.playerResult);
      if (data.dealerResult) setDealerResultName(data.dealerResult);
      if (data.result) setResult(data.result);
      if (typeof data.winnings === 'number') setWinnings(data.winnings);
      if (typeof data.newBalance === 'number') setBalance(data.newBalance);

      // Manejo de sesión expirada opcional (si el backend lo envía como 'void/expired')
      if (data.expired) {
        setError(
          t('cardPoker.session_expired', {
            defaultValue: 'Session expired. Ante has been refunded.',
          })
        );
      }
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        t('cardPoker.error_resolve', { defaultValue: 'Error resolving Card Poker' });
      setError(msg);
    } finally {
      setResolving(false);
    }
  };

  // ===== Historial =====
  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    setError('');
    try {
      const rows = await getCardPokerHistory();
      setHistory(Array.isArray(rows) ? rows : []);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        t('cardPoker.error_history', { defaultValue: 'Error fetching history' });
      setError(msg);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Badge de resultado
  const ResultBadge = () => {
    if (!result) return null;
    const mapColor = { win: 'success', tie: 'warning', lose: 'danger', void: 'secondary' };
    const mapText = {
      win: t('cardPoker.result.win', { defaultValue: 'You win!' }),
      tie: t('cardPoker.result.tie', { defaultValue: 'Tie' }),
      lose: t('cardPoker.result.lose', { defaultValue: 'You lose' }),
      void: t('cardPoker.result.void', { defaultValue: 'Void' }),
    };
    return <span className={`badge bg-${mapColor[result]}`}>{mapText[result]}</span>;
  };

  return (
    <div className="cpkr-container d-flex justify-content-center align-items-start">
      <Card className="cpkr-card shadow-lg">
        <Card.Body>
          {/* Encabezado */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button
              variant="secondary"
              onClick={() => navigate('/sessions')}
              title={t('common.back', { defaultValue: 'Back' })}
            >
              <FaArrowLeft className="me-2" />
              {t('common.back', { defaultValue: 'Back' })}
            </Button>

            <h2 className="m-0">
              <FaCrown className="text-warning me-2" />
              {t('cardPoker.title', { defaultValue: 'Card Poker' })}
              <FaCrown className="text-warning ms-2" />
            </h2>

            <div className="d-flex gap-2">
              <Button variant="outline-info" onClick={openHistory}>
                <FaHistory className="me-2" />
                {t('cardPoker.history', { defaultValue: 'History' })}
              </Button>
              <Button variant="outline-secondary" onClick={() => setShowRules(true)}>
                <FaInfoCircle className="me-2" />
                {t('cardPoker.rules', { defaultValue: 'Rules' })}
              </Button>
            </div>
          </div>

          {/* Barra: Apuesta / Start / Reset / Saldo */}
          <div className="cpkr-toolbar">
            <InputGroup style={{ maxWidth: 240 }}>
              <InputGroup.Text>{t('cardPoker.bet', { defaultValue: 'Bet' })}</InputGroup.Text>
              <FormControl
                type="number"
                min={1}
                max={1_000_000}
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
              />
              <InputGroup.Text>{t('cardPoker.currency', { defaultValue: 'MENEL' })}</InputGroup.Text>
            </InputGroup>

            <div className="d-flex gap-2">
              <Button onClick={handleStart} disabled={starting || inRound}>
                {starting ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <FaPlay className="me-2" />
                    {t('cardPoker.start', { defaultValue: 'Start' })}
                  </>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={resetRound}
                disabled={starting || (!inRound && !roundResolved)}
              >
                <FaRedo className="me-2" />
                {t('cardPoker.reset', { defaultValue: 'Reset' })}
              </Button>
            </div>

            <div className="cpkr-balance">
              {t('cardPoker.balance', { defaultValue: 'Balance' })}:{' '}
              <strong>{balance ?? '—'}</strong> {t('cardPoker.currency', { defaultValue: 'MENEL' })}
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}

          {/* Mano en curso / showdown */}
          {playerHand.length > 0 && (
            <>
              <Row className="mt-4">
                <Col md={7}>
                  <h5 className="mb-2">
                    {t('cardPoker.player_hand', { defaultValue: 'Your hand' })}
                    {playerResultName && roundResolved ? ` — ${playerResultName}` : ''}
                  </h5>
                  <div className="cpkr-cards">
                    {playerHand.map((c, i) => (
                      <img
                        key={`${c.card}-${i}`}
                        className="cpkr-card-img"
                        src={getCardImage(c.card)}
                        alt={c.card}
                      />
                    ))}
                  </div>
                </Col>

                <Col md={5}>
                  <h5 className="mb-2">
                    {t('cardPoker.dealer', { defaultValue: 'Dealer' })}
                    {roundResolved && dealerResultName ? ` — ${dealerResultName}` : ''}
                  </h5>

                  {!roundResolved ? (
                    <div className="cpkr-cards">
                      {dealerUpcard ? (
                        <img
                          className="cpkr-card-img"
                          src={getCardImage(dealerUpcard.card)}
                          alt={dealerUpcard.card}
                        />
                      ) : null}
                      {[...Array(4)].map((_, idx) => (
                        <div key={idx} className="cpkr-card-back" aria-hidden />
                      ))}
                    </div>
                  ) : (
                    <div className="cpkr-cards">
                      {dealerHand.map((c, i) => (
                        <img
                          key={`${c.card}-${i}`}
                          className="cpkr-card-img"
                          src={getCardImage(c.card)}
                          alt={c.card}
                        />
                      ))}
                    </div>
                  )}
                </Col>
              </Row>

              {/* Acciones mientras la ronda está viva */}
              {!roundResolved && (
                <div className="d-flex justify-content-center mt-4">
                  <Button variant="success" onClick={handleResolve} disabled={resolving}>
                    {resolving ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <FaCheck className="me-2" />
                        {t('cardPoker.resolve', { defaultValue: 'Resolve' })}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Resultado final */}
          {roundResolved && (
            <div className="mt-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <ResultBadge />
              </div>
              <div>
                {t('cardPoker.winnings', { defaultValue: 'Winnings' })}:{' '}
                <strong>{typeof winnings === 'number' ? winnings : 0}</strong>{' '}
                {t('cardPoker.currency', { defaultValue: 'MENEL' })}
              </div>
              <div>
                {t('cardPoker.new_balance', { defaultValue: 'New balance' })}:{' '}
                <strong>{balance ?? '—'}</strong> {t('cardPoker.currency', { defaultValue: 'MENEL' })}
              </div>

              <div className="d-flex gap-2 mt-3">
                <Button onClick={handleStart} disabled={starting || inRound}>
                  {starting ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <FaPlay className="me-2" />
                      {t('cardPoker.play_again', { defaultValue: 'Play again' })}
                    </>
                  )}
                </Button>
                <Button variant="outline-secondary" onClick={resetRound}>
                  <FaRedo className="me-2" />
                  {t('cardPoker.reset', { defaultValue: 'Reset' })}
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal: Reglas */}
      <Modal show={showRules} onHide={() => setShowRules(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaInfoCircle className="me-2" />
            {t('cardPoker.rules', { defaultValue: 'Rules' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="mb-0">
            <li>
              <strong>{t('cardPoker.rule_ante', { defaultValue: 'Ante' })}:</strong>{' '}
              {t('cardPoker.rule_ante_text', {
                defaultValue: 'Pay the Ante to receive 5 cards. Dealer also draws 5 cards but shows only one.',
              })}
            </li>
            <li>
              <strong>{t('cardPoker.rule_decision', { defaultValue: 'Decision' })}:</strong>{' '}
              {t('cardPoker.rule_decision_text', {
                defaultValue:
                  'Press Resolve to go to showdown. Standard 5-card poker ranking decides the winner.',
              })}
            </li>
            <li>
              <strong>{t('cardPoker.rule_payouts', { defaultValue: 'Payouts' })}:</strong>{' '}
              {t('cardPoker.rule_payouts_text', {
                defaultValue: 'Win pays 1:1 (returns Ante + profit equal to bet). Tie pushes (Ante returned). Lose: lose Ante.',
              })}
            </li>
          </ul>
        </Modal.Body>
      </Modal>

      {/* Modal: Historial */}
      <Modal show={showHistory} onHide={() => setShowHistory(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaHistory className="me-2" />
            {t('cardPoker.history', { defaultValue: 'History' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 420, overflowY: 'auto' }}>
          {historyLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner />
            </div>
          ) : history.length ? (
            <ul className="list-group">
              {history.map((h, idx) => (
                <li key={idx} className="list-group-item">
                  <div>
                    <strong>{t('cardPoker.result_label', { defaultValue: 'Result' })}:</strong>{' '}
                    {h.result}
                  </div>
                  {'score' in h && (
                    <div>
                      <strong>{t('cardPoker.score', { defaultValue: 'Score' })}:</strong> {h.score}
                    </div>
                  )}
                  <div>
                    <strong>{t('cardPoker.details', { defaultValue: 'Details' })}:</strong> {h.details}
                  </div>
                  <div>
                    <strong>{t('cardPoker.date', { defaultValue: 'Date' })}:</strong>{' '}
                    {new Date(h.created_at).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-muted">
              {t('cardPoker.no_history', { defaultValue: 'No records yet.' })}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CardPoker;
